// INV2 (docs/backlog.md, PRD P0.7). Pure, no DOM (see test/ui-has-
// permission.test.ts's own header for why a plain node:test file can
// import a ui/src module directly): none of the functions below touch
// `window` or `document`. Two small, independently testable pieces of
// the counting walk's own logic, factored out so the blind-mode-hides
// and tap-a-tenth behaviors are provable without a browser.

/**
 * What the walk's "Currently on hand" status line should say for one
 * product, or that it should say NOTHING at all.
 *
 * Decided, not guessed (see docs/build-briefs/INV2-count-session-
 * governance.md): blind mode hides the expected on-hand figure from the
 * counting WALK only. Backbar's own blind-count design hides the
 * expected number from the person physically counting; the review-
 * before-close screen exists specifically to show counted-versus-
 * expected before a variance posts, so this function is never called for
 * that screen at all (the review renders unconditionally from its own
 * variance data, admin.ts's own renderCountReviewView).
 *
 * Returns `undefined` (not an empty string, not a placeholder like
 * "Hidden") when blind: the caller must render NOTHING for the on-hand
 * row at all, so a blind counter reading the DOM directly (not merely
 * looking at a CSS-hidden element) cannot recover the expected quantity
 * either.
 */
export function buildCountWalkProductStatus(params: {
  onHandQuantity: string | null;
  onHandUnit: string | null;
  blindMode: boolean;
}): string | undefined {
  if (params.blindMode) {
    return undefined;
  }
  if (params.onHandQuantity === null) {
    // mixedUnits or genuinely unknown: nothing meaningful to show, but
    // this is a DIFFERENT case from blind mode (an honest "we don't
    // know" versus a deliberate withholding), so the caller may still
    // choose to render an explanatory line; this function itself has
    // nothing to add.
    return undefined;
  }
  const unit = params.onHandUnit ?? "";
  return `Currently on hand: ${params.onHandQuantity}${unit.length > 0 ? ` ${unit}` : ""}.`;
}

/** The ten tap targets a partial-bottle observation offers: 0.0 through
 * 0.9, one tenth apart. Backbar's own tap-a-tenth partial-bottle control
 * is the named model (docs/backbar-shift4-parity-matrix.md); this is
 * simply the ordered list of values that control renders, kept here so
 * it is testable without a DOM. */
export const BOTTLE_TENTH_OPTIONS: readonly number[] = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];

/**
 * Combines a whole-bottle stepper value and a tapped tenth into the
 * exact decimal string recordCount receives, e.g. (1, 3) -> "1.3", (0, 0)
 * -> "0". Never a typed free decimal: both inputs are integers chosen by
 * tap (a stepper and a row of ten buttons), so the RESULT is always an
 * exact multiple of 0.1 by construction, matching
 * requireBottleTenthIncrement's own server-side rule exactly rather than
 * merely hoping the two agree.
 */
export function combineWholeBottlesAndTenths(wholeBottles: number, tenth: number): string {
  if (!Number.isInteger(wholeBottles) || wholeBottles < 0) {
    throw new Error(`wholeBottles must be a non-negative integer; received ${wholeBottles}`);
  }
  if (!Number.isInteger(tenth) || tenth < 0 || tenth > 9) {
    throw new Error(`tenth must be an integer from 0 to 9; received ${tenth}`);
  }
  return tenth === 0 ? String(wholeBottles) : `${wholeBottles}.${tenth}`;
}

/** UX102 (click-through review, Counting sheet): ounces read to one decimal place ("132.3", never "132.2562").
 * A value that is not a plain number is returned as it came. */
export function roundOuncesForDisplay(ounces: string): string {
  const value = Number(ounces);
  return Number.isFinite(value) ? String(Math.round(value * 10) / 10) : ounces;
}

/** CORE8 (core walkthrough 2026-10-10, item 11): a count figure read to its unit's own precision. Ounces to one decimal
 * place (UX102), bottles and units to at most two with no trailing zeros, so a whole count reads "3" and never
 * "3.0000". A value that is not a plain number is returned as it came. */
export function roundCountQuantityForDisplay(quantity: string, unit: string): string {
  const value = Number(quantity);
  if (!Number.isFinite(value)) {
    return quantity;
  }
  const scale = unit === "oz" ? 10 : 100;
  return String(Math.round(value * scale) / scale);
}

/** CORE8: a signed variance to the same precision. Exactly zero reads "No change"; a nonzero amount too small to show
 * keeps its sign and reads "+<0.1 oz" or "-<0.01 bottles", never "No change" (a real shortage is not hidden). */
export function formatCountVarianceForDisplay(quantity: string, unit: string): string {
  const value = Number(quantity);
  if (!Number.isFinite(value)) {
    return quantity;
  }
  if (value === 0) {
    return "No change";
  }
  const shown = roundCountQuantityForDisplay(quantity, unit);
  const sign = value > 0 ? "+" : "";
  const label = countUnitLabel(unit, shown);
  if (Number(shown) === 0) {
    const floor = unit === "oz" ? "0.1" : "0.01";
    return `${value > 0 ? "+" : "-"}<${floor} ${countUnitLabel(unit, floor)}`;
  }
  return `${sign}${shown} ${label}`;
}

/** The unit word for a rounded figure: "oz", "bottle(s)", "unit(s)"; any other unit as it is. */
export function countUnitLabel(unit: string, shownQuantity: string): string {
  if (unit === "oz") return "oz";
  const one = Math.abs(Number(shownQuantity)) === 1;
  if (unit === "bottle") return one ? "bottle" : "bottles";
  if (unit === "unit") return one ? "unit" : "units";
  if (unit === "keg") return one ? "keg" : "kegs";
  if (unit === "can") return one ? "can" : "cans";
  return unit;
}

/** CORE8: "132.3 oz", "3 bottles", for a counted or expected figure. */
export function formatCountQuantityForDisplay(quantity: string, unit: string): string {
  const shown = roundCountQuantityForDisplay(quantity, unit);
  return `${shown} ${countUnitLabel(unit, shown)}`;
}

/** CORE8: a server refusal shown on a count screen must never carry an id. A message with a UUID-shaped id in it is
 * replaced by the plain fallback; any other message is kept as it came. */
export function plainCountMessage(message: string | undefined, fallback: string): string {
  if (message === undefined || message.length === 0) {
    return fallback;
  }
  return /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i.test(message)
    ? fallback
    : message;
}

/** CORE8: the sentence for a save refused because the count belongs to someone else. */
export function countHeldByOtherMessage(holderName: string, canTakeOver: boolean): string {
  return canTakeOver
    ? `${holderName} has this count. Take over to count it yourself.`
    : `${holderName} has this count. Ask a manager to reassign it.`;
}
